import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../api.ts';
import { addMinutes, fromInputs, roundQuarter, toDateInput, toTimeInput } from '../time.ts';
import { ErrorText } from './ErrorText.tsx';
import { Modal } from './Modal.tsx';

interface Free {
  id: string;
  name: string;
  floor: number;
  capacity: number;
}

export function FindRoomDialog({
  day,
  onBook,
  onClose,
}: {
  day: Date;
  onBook: (p: { roomId: string; start: Date; end: Date; attendees: number }) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const base =
    addMinutes(day, 9 * 60) > new Date()
      ? addMinutes(day, 9 * 60)
      : roundQuarter(addMinutes(new Date(), 15));
  const [people, setPeople] = useState('4');
  const [date, setDate] = useState(toDateInput(base));
  const [start, setStart] = useState(toTimeInput(base));
  const [end, setEnd] = useState(toTimeInput(addMinutes(base, 60)));
  const [result, setResult] = useState<Free[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const search = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const s = fromInputs(date, start);
    const en = fromInputs(date, end);
    if (!s || !en) return;
    try {
      setResult(
        await api.get<Free[]>(
          `/api/rooms/free?${qs({ start: s.toISOString(), end: en.toISOString(), attendees: people })}`,
        ),
      );
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Modal title={t('find.title')} onClose={onClose}>
      <form onSubmit={search}>
        <div className="row">
          <label>
            {t('booking.attendees')}
            <input
              type="number"
              min={1}
              value={people}
              onChange={(e) => setPeople(e.target.value)}
            />
          </label>
          <label>
            {t('common.date')}
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>
        <div className="row">
          <label>
            {t('common.start')}
            <input
              type="time"
              step={900}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </label>
          <label>
            {t('common.end')}
            <input type="time" step={900} value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>
        <button className="btn">{t('find.search')}</button>
      </form>
      <ErrorText error={error} />
      <div aria-live="polite" style={{ margin: '12px 0' }}>
        {result?.length === 0 && <p className="k">{t('find.none')}</p>}
        {result?.map((r) => (
          <div className="li" key={r.id}>
            <span>
              <b>{r.name}</b> · {t('common.floor')} {r.floor} · {r.capacity} {t('common.people')}
            </span>
            <button
              className="btn sm"
              onClick={() =>
                onBook({
                  roomId: r.id,
                  start: fromInputs(date, start)!,
                  end: fromInputs(date, end)!,
                  attendees: Number(people),
                })
              }
            >
              {t('find.book')}
            </button>
          </div>
        ))}
      </div>
      <button className="btn alt" onClick={onClose}>
        {t('common.close')}
      </button>
    </Modal>
  );
}
